import React, { useEffect, useState } from "react";
import { X, ExternalLink, Download, FileText } from "lucide-react";
import { getDocumentMetadata, downloadDocument } from "../../../../utils/documentHelpers";

const DocumentPreviewModal = ({ doc, onClose }) => {
  const [meta, setMeta] = useState({ name: "", url: "", blobUrl: "", isPdf: false });
  const [iframeError, setIframeError] = useState(false);

  useEffect(() => {
    if (doc) {
      const data = getDocumentMetadata(doc);
      setMeta(data);
      setIframeError(false);

      return () => {
        if (data.blobUrl && data.blobUrl.startsWith("blob:")) {
          URL.revokeObjectURL(data.blobUrl);
        }
      };
    }
  }, [doc]);

  if (!doc) return null;

  const targetUrl = meta.blobUrl || meta.url;

  const handleOpenInNewTab = (e) => {
    if (e) e.preventDefault();
    const targetUrl = meta.blobUrl || meta.url;
    if (!targetUrl) return;

    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl overflow-hidden border border-slate-200">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 max-w-md truncate">
            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <h3 className="font-bold text-slate-800 text-sm truncate" title={meta.name}>
              {meta.name}
            </h3>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => downloadDocument(doc)}
              className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-blue-600 font-semibold px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 transition-colors shadow-sm"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Download</span>
            </button>

            <button
              onClick={handleOpenInNewTab}
              className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-semibold px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50/60 hover:bg-blue-100/80 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span>Open in new tab</span>
            </button>

            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors ml-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Content / Preview Frame */}
        <div className="flex-1 bg-slate-900 p-2 overflow-hidden flex flex-col items-center justify-center relative">
          {meta.isPdf && !iframeError ? (
            <iframe
              src={targetUrl}
              title={meta.name}
              className="w-full h-full rounded-xl border-0 bg-white"
              onError={() => setIframeError(true)}
            />
          ) : (
            <div className="p-8 text-center text-slate-200 max-w-md bg-slate-800/90 rounded-2xl border border-slate-700 shadow-xl flex flex-col items-center gap-4">
              <FileText className="w-16 h-16 text-blue-400" />
              <div>
                <h4 className="font-bold text-lg text-white mb-1">{meta.name}</h4>
                <p className="text-xs text-slate-400">
                  {meta.isPdf
                    ? "Your browser blocked embedding this PDF directly."
                    : "Direct preview is available for PDF documents."}
                </p>
              </div>
              <div className="flex gap-3 mt-2">
                <button
                  onClick={handleOpenInNewTab}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5"
                >
                  <ExternalLink className="w-4 h-4" /> Open PDF
                </button>
                <button
                  onClick={() => downloadDocument(doc)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5"
                >
                  <Download className="w-4 h-4" /> Download PDF
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
